const state = {
  searchResults: {
    products: [],
    hasMore: false,
    query: ''
  },
  loading: false,
  error: null,
  imageData: null,
  fromCache: false,
  headlines: [],
  shareId: null,
  isSharedResults: false
};

const getters = {
  searchResults: state => state.searchResults,
  isLoading: state => state.loading,
  getError: state => state.error,
  getImageData: state => state.imageData,
  isFromCache: state => state.fromCache,
  getHeadlines: state => state.headlines,
  getShareId: state => state.shareId,
  isSharedResults: state => state.isSharedResults
};

const mutations = {
  setSearchResults(state, results) {
    // Add debugging to inspect results
    console.log('Setting search results, first product:', results.products && results.products.length > 0 ? {
      name: results.products[0].name,
      scores: results.products[0].scores,
      overall: results.products[0].scores ? results.products[0].scores.overall : 'missing'
    } : 'No products');
    
    // Ensure we have a valid results object
    const validResults = {
      products: results.products || [],
      hasMore: !!results.hasMore,
      query: results.query || ''
    };

    if (results.loadMore) {
      // Merge with existing results
      state.searchResults = {
        products: [...state.searchResults.products, ...validResults.products],
        hasMore: validResults.hasMore,
        query: validResults.query || state.searchResults.query
      };
    } else {
      // Set new results
      state.searchResults = validResults;
    }
    
    // Update fromCache flag if provided
    if (results.fromCache !== undefined) {
      state.fromCache = results.fromCache;
    }

    // Set shareId if provided
    if (results.shareId) {
      state.shareId = results.shareId;
    }

    // Set isSharedResults flag if this is from a shared link
    if (results.isSharedResults !== undefined) {
      state.isSharedResults = results.isSharedResults;
    }
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setError(state, error) {
    state.error = error;
  },
  setImageData(state, data) {
    state.imageData = data;
  },
  setFromCache(state, fromCache) {
    state.fromCache = fromCache;
  },
  setHeadlines(state, headlines) {
    state.headlines = headlines;
  },
  setShareId(state, shareId) {
    state.shareId = shareId;
  },
  setIsSharedResults(state, isShared) {
    state.isSharedResults = isShared;
  }
};

const actions = {
  async search({ commit, rootState }, { query, imageData, loadMore = false, shareId = null }) {
    commit('setLoading', true);
    commit('setError', null);
    
    try {
      // Debug log the state
      console.log('Current preferences in store before search:', {
        ethical: rootState.preferences?.ethicalPreferences,
        political: rootState.preferences?.politicalPreferences,
        weights: rootState.preferences?.weightPreferences
      });
      
      // Get user preferences with all values
      const userPreferences = {
        ethical: rootState.preferences?.ethicalPreferences || {},
        political: rootState.preferences?.politicalPreferences || {},
        weights: {
          price: rootState.preferences?.weightPreferences?.price || 5,
          quality: rootState.preferences?.weightPreferences?.quality || 5,
          ethical: rootState.preferences?.weightPreferences?.ethical || 5,
          political: rootState.preferences?.weightPreferences?.political || 0
        }
      };

      // Log the complete preferences for debugging
      console.log('Complete user preferences being sent to server:', JSON.stringify(userPreferences, null, 2));

      // Get existing product IDs to exclude when loading more
      let excludeProducts = [];
      if (loadMore && state.searchResults && state.searchResults.products) {
        // Create a unique identifier for each product using manufacturer + name or just id if available
        excludeProducts = state.searchResults.products.map(product => {
          // If the product has an id, use that
          if (product.id) {
            return product.id;
          }
          
          // If product has both manufacturer and name, combine them for a more unique identifier
          if (product.manufacturer && product.name) {
            return `${product.manufacturer} ${product.name}`;
          }
          
          // Fallback to just the name
          return product.name;
        });
        
        console.log(`Excluding ${excludeProducts.length} previously loaded products:`, excludeProducts);
      }

      // Call the Firebase function
      const response = await fetch('/api/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          query,
          imageData,
          loadMore,
          excludeProducts,
          userPreferences,
          fetchHeadlines: !loadMore,
          shareId // Pass the shareId to the backend if provided
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        
        let errorMessage = 'Search request failed';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
        }
        
        throw new Error(errorMessage);
      }

      const responseText = await response.text();
      console.log('Raw API response:', responseText);
      
      let results;
      try {
        results = JSON.parse(responseText);
        
        // More detailed debug logging of the parsed result
        if (results && results.products && results.products.length > 0) {
          const firstProduct = results.products[0];
          console.log('API Response - First product full object:', JSON.stringify(firstProduct, null, 2));
          console.log('API Response - First product scores object:', JSON.stringify(firstProduct.scores, null, 2));
        }
      } catch (parseError) {
        console.error('Failed to parse API response:', parseError, responseText);
        throw new Error('Failed to parse search results');
      }
      
      // Process headlines if available and not loading more
      if (!loadMore && results.headlines && Array.isArray(results.headlines) && results.headlines.length > 0) {
        console.log('Headlines received from API:', results.headlines);
        commit('setHeadlines', results.headlines);
      } else {
        console.log('No valid headlines received from API:', { 
          loadMore, 
          hasHeadlines: !!results.headlines, 
          isArray: Array.isArray(results.headlines || []),
          count: results.headlines ? results.headlines.length : 0
        });
        // Clear any existing headlines
        commit('setHeadlines', []);
      }
      
      // Debug log to check if political scores are present
      if (results && results.products && results.products.length > 0) {
        const firstProduct = results.products[0];
        console.log('First product scores:', firstProduct.scores);
        console.log('Political score present:', !!firstProduct.scores.political);
        console.log('Political weights set:', !!userPreferences.weights.political);
        
        // Debug log for overall score
        console.log('First product overall score:', firstProduct.scores.overall, typeof firstProduct.scores.overall);
        
        // Check all products for score issues
        results.products.forEach((product, index) => {
          if (!product.scores || typeof product.scores.overall === 'undefined' || product.scores.overall === null) {
            console.error(`Product ${index} missing overall score:`, product.name, product.scores);
          }
        });
      }
      
      // Calculate overall scores if missing
      if (results && results.products) {
        results.products = results.products.map(product => {
          if (!product.scores) {
            product.scores = {
              price: 5,
              quality: 5,
              ethical: 5
            };
          }
          
          // If overall score is missing, calculate it based on the weights and individual scores
          if (typeof product.scores.overall === 'undefined' || product.scores.overall === null) {
            // Get the weights from user preferences, or use default weights if not available
            const weights = userPreferences?.weights || {
              price: 5,
              quality: 5,
              ethical: 5,
              political: 0
            };
            
            // For the calculation, we'll distribute the ethical/political weight between the two scores
            let ethicalPoliticalWeight = weights.ethical || 5; // Use the ethical slider value for the combined weight
            
            // Calculate weights for components
            const priceWeight = weights.price || 5;
            const qualityWeight = weights.quality || 5;
            
            // Distribute the ethical-political weight between ethical and political if political score exists
            let effectiveEthicalWeight = ethicalPoliticalWeight;
            let effectivePoliticalWeight = 0;
            
            // If political score exists and is requested through preferences, split the ethical-political weight
            if (product.scores.political && (weights.political > 0 || userPreferences?.politicalPreferences)) {
              // Split the weight between ethical and political aspects
              effectiveEthicalWeight = ethicalPoliticalWeight / 2;
              effectivePoliticalWeight = ethicalPoliticalWeight / 2;
            }
            
            // Calculate total weight
            const totalWeight = priceWeight + qualityWeight + effectiveEthicalWeight + effectivePoliticalWeight;
            
            // Calculate weighted average
            let weightedSum = 
              (product.scores.price * priceWeight) +
              (product.scores.quality * qualityWeight) +
              (product.scores.ethical * effectiveEthicalWeight);
              
            // Add political component if available
            if (product.scores.political && effectivePoliticalWeight > 0) {
              weightedSum += (product.scores.political * effectivePoliticalWeight);
            }
            
            // Assign the calculated overall score
            product.scores.overall = totalWeight > 0 ? weightedSum / totalWeight : 0;
            
            // Generate explanation for how score was calculated (for tooltip)
            const weightExplanation = {
              price: {
                score: product.scores.price,
                weight: priceWeight,
                contribution: ((product.scores.price * priceWeight) / totalWeight).toFixed(1)
              },
              quality: {
                score: product.scores.quality,
                weight: qualityWeight,
                contribution: ((product.scores.quality * qualityWeight) / totalWeight).toFixed(1)
              },
              ethical: {
                score: product.scores.ethical,
                weight: effectiveEthicalWeight,
                contribution: ((product.scores.ethical * effectiveEthicalWeight) / totalWeight).toFixed(1)
              }
            };
            
            if (product.scores.political && effectivePoliticalWeight > 0) {
              weightExplanation.political = {
                score: product.scores.political,
                weight: effectivePoliticalWeight,
                contribution: ((product.scores.political * effectivePoliticalWeight) / totalWeight).toFixed(1)
              };
            }
            
            // Store the explanation with the product for tooltip use
            product.scoreExplanation = weightExplanation;
            
            console.log(`Calculated overall score for ${product.name}:`, product.scores.overall);
            console.log(`Score explanation:`, weightExplanation);
          }
          
          return product;
        });
      }
      
      // Process retailer information
      if (results && results.products) {
        results.products.forEach(product => {
          if (product.retailers && Array.isArray(product.retailers)) {
            product.retailers = product.retailers.map(retailer => {
              // Ensure domain is present
              if (!retailer.domain && retailer.url) {
                try {
                  const urlObj = new URL(retailer.url);
                  retailer.domain = urlObj.hostname.replace(/^www\./, '');
                } catch (e) {
                  retailer.domain = 'unknown';
                }
              }
              
              // Set isManufacturerSite if not already set
              if (retailer.isManufacturerSite === undefined) {
                // Check if the domain contains any part of the manufacturer name
                if (product.manufacturer && retailer.domain) {
                  const manufacturerWords = product.manufacturer.toLowerCase().split(/\s+/);
                  const domainLower = retailer.domain.toLowerCase();
                  
                  // Check if any word from manufacturer name appears in the domain
                  retailer.isManufacturerSite = manufacturerWords.some(word => 
                    word.length > 2 && domainLower.includes(word)
                  );
                } else {
                  retailer.isManufacturerSite = false;
                }
              }
              
              return retailer;
            });
          }
        });
      }
      
      // Update fromCache flag in the store
      if (results.fromCache !== undefined) {
        commit('setFromCache', results.fromCache);
        
        // Log whether the results came from cache
        console.log(`Results came from ${results.fromCache ? 'cache' : 'fresh search'}`);
      }
      
      // If it's not a loadMore request and not fetching by shareId, generate a shareId
      if (!loadMore && !shareId && results && results.products && results.products.length > 0) {
        // If the backend didn't provide a shareId, generate one on the client
        if (!results.shareId) {
          // Simple UUID generation
          results.shareId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
          });
          
          // Save this result with shareId to cache
          try {
            // Send the shareId to be saved with this search result
            await fetch('/api/save-shared-search', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                shareId: results.shareId,
                query,
                results
              })
            });
            
            console.log('Search results saved with shareId:', results.shareId);
          } catch (saveError) {
            console.error('Failed to save shared search:', saveError);
            // We continue even if saving fails
          }
        }
      }
      
      // Mark as shared results if fetched by shareId
      if (shareId) {
        results.isSharedResults = true;
      }
      
      if (loadMore) {
        // Merge with existing results
        const currentResults = state.searchResults;
        commit('setSearchResults', {
          products: [...currentResults.products, ...results.products],
          hasMore: results.hasMore,
          query: results.query,
          fromCache: results.fromCache,
          shareId: currentResults.shareId || results.shareId,
          isSharedResults: state.isSharedResults
        });
      } else {
        // Set new results
        commit('setSearchResults', {
          ...results,
          fromCache: results.fromCache,
          shareId: results.shareId,
          isSharedResults: !!shareId
        });
      }
    } catch (error) {
      console.error('Search error:', error);
      commit('setError', error.message || 'Search failed');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // New action to fetch search results by shareId
  async fetchSharedSearch({ commit, dispatch }, shareId) {
    if (!shareId) {
      commit('setError', 'Invalid share link');
      return false;
    }
    
    try {
      commit('setLoading', true);
      
      // Get the query parameter from the URL to pass to the backend
      const urlParams = new URLSearchParams(window.location.search);
      const queryFromUrl = urlParams.get('q') || '';
      
      // Call the search action with the shareId and query from URL
      await dispatch('search', { 
        query: queryFromUrl,
        shareId 
      });
      
      return true;
    } catch (error) {
      console.error('Error fetching shared search:', error);
      commit('setError', 'Could not load shared search results');
      return false;
    }
  },
  
  async identifyProductFromImage({ commit }, imageData) {
    if (!imageData) {
      commit('setError', 'No image data provided');
      return null;
    }
    
    commit('setLoading', true);
    commit('setError', null);
    
    try {
      // Call the Firebase function with a special flag to only identify the product
      const response = await fetch('/api/identify-product', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          imageData
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to identify product');
      }

      const result = await response.json();
      return result.productName || null;
    } catch (error) {
      console.error('Product identification error:', error);
      commit('setError', error.message || 'Failed to identify product');
      return null;
    } finally {
      commit('setLoading', false);
    }
  },
  
  clearResults({ commit }) {
    commit('setSearchResults', {
      products: [],
      hasMore: false,
      query: ''
    });
    commit('setError', null);
    commit('setHeadlines', []);
    commit('setShareId', null);
    commit('setIsSharedResults', false);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}; 